import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePublishedUpdates, useMyUpdateViews, useMarkUpdateSeen, useMarkUpdateDismissed, useMarkUpdateClicked, UPDATE_TYPE_LABELS, resolveUpdateContent } from "@/hooks/useAppUpdates";
import type { AppUpdate } from "@/hooks/useAppUpdates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";

const UpdatePopup = () => {
  const { user } = useAuth();
  const { locale } = useI18n();
  const { data: updates = [] } = usePublishedUpdates();
  const { data: views = [] } = useMyUpdateViews();
  const markSeen = useMarkUpdateSeen();
  const markDismissed = useMarkUpdateDismissed();
  const markClicked = useMarkUpdateClicked();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<AppUpdate | null>(null);
  const [shown, setShown] = useState(false);
  const [markedSeenId, setMarkedSeenId] = useState<string | null>(null);

  // Find the first unseen popup update
  useEffect(() => {
    if (!user || shown || updates.length === 0) return;

    const seenIds = new Set(views.map(v => v.update_id));
    const popupUpdate = updates.find(u =>
      u.display_popup && u.priority === "high" && !seenIds.has(u.id)
    ) || updates.find(u =>
      u.display_popup && !seenIds.has(u.id)
    );

    if (popupUpdate && popupUpdate.id !== markedSeenId) {
      // Small delay so it doesn't flash immediately on page load
      const t = setTimeout(() => {
        setCurrent(popupUpdate);
        setMarkedSeenId(popupUpdate.id);
        markSeen.mutate({ updateId: popupUpdate.id, method: "popup" });
        setShown(true);
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [user, updates, views, shown, markedSeenId]);

  const handleDismiss = useCallback(() => {
    if (current) markDismissed.mutate(current.id);
    setCurrent(null);
  }, [current]);

  const handleAction = useCallback(() => {
    if (!current) return;
    markClicked.mutate(current.id);
    if (current.action_url) navigate(current.action_url);
    setCurrent(null);
  }, [current, navigate]);

  if (!current) return null;

  const typeInfo = UPDATE_TYPE_LABELS[current.update_type] || UPDATE_TYPE_LABELS.improvement;
  const content = resolveUpdateContent(current, locale);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span className="text-2xl">{typeInfo.emoji}</span>
            {content.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge className={`${typeInfo.color} border-0`}>{typeInfo.label}</Badge>
            {current.version && <span className="text-xs text-muted-foreground">v{current.version}</span>}
          </div>
          {content.short_description && (
            <p className="text-muted-foreground text-sm">{content.short_description}</p>
          )}
          {content.full_description && (
            <p className="text-sm whitespace-pre-wrap line-clamp-6">{content.full_description}</p>
          )}
          <div className="flex gap-2">
            {current.action_label && current.action_url ? (
              <Button onClick={handleAction} className="flex-1 rounded-xl">
                {current.action_label} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : null}
            <Button variant="outline" onClick={handleDismiss} className="rounded-xl">
              Stäng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdatePopup;
